import { merge, of, Subject } from 'rxjs';
import { catchError, map, startWith, switchMap, takeUntil } from 'rxjs/operators';

import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import { SnAbilityService } from '@lib/ability';
import { SortParameters } from '@lib/api';
import { snFadeIn, snFadeOut } from '@lib/common/animations';

import {
  LprHotListAPI,
  LprHotlistGroupsAPI,
  SnLpr,
  SnLprHotListGroup,
  SnLprObject,
} from '../../api';
import { AddHotlistGroupDialogComponent } from '../add-hotlist-group-dialog/add-hotlist-group-dialog.component';
import { ManageLprGroupDialogComponent } from '../manage-lpr-group-dialog/manage-lpr-group-dialog.component';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'fs-lpr-hotlist-groups',
  templateUrl: './lpr-hotlist-groups.component.html',
  styleUrls: ['./lpr-hotlist-groups.component.scss'],
  animations: [snFadeIn, snFadeOut],
})
export class LprHotlistGroupsComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input()
  public lprHotlist: SnLpr;

  @Input()
  public lprObjects: SnLprObject[];

  @ViewChild(MatPaginator) public paginator: MatPaginator;
  @ViewChild(MatSort) public matSort: MatSort;

  public get columns(): string[] {
    return this._getColumns();
  }

  public readonly canCreate: boolean;
  public readonly canChange: boolean;
  public readonly canDelete: boolean;
  public sort: string = 'name';
  public sortDirection: SortDirection = 'asc';
  public resultsLength = 0;
  public pageSize: number = 10;
  public dataSource: MatTableDataSource<SnLprHotListGroup>;
  public isLoading: boolean = false;

  public hotlistId: string = '';
  public groupIsNull: boolean = false;

  private _searchQuery: string;
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _dialog: MatDialog,
    private readonly _lprHotlistGroupsAPI: LprHotlistGroupsAPI,
    private readonly _lprPlatesAPI: LprHotListAPI,
    ability: SnAbilityService,
  ) {
    this.canCreate = ability.can({
      subject: 'lpr/lprhotlistobjectgroup',
      action: 'add',
    });
    this.canChange = ability.can({
      subject: 'lpr/lprhotlistobjectgroup',
      action: 'change',
    });
    this.canDelete = ability.can({
      subject: 'lpr/lprhotlistobjectgroup',
      action: 'delete',
    });
  }

  public ngOnInit(): void {}

  public ngAfterViewInit(): void {
    const id = this.lprHotlist.id;
    this._fetchData(id);
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onSortChange(sort: Sort): void {
    this.sort = sort.active;
    this.sortDirection = sort.direction;
  }

  public removeGroup(group: SnLprHotListGroup, id: string): void {
    this._lprHotlistGroupsAPI
      .delete(group.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._fetchData(id);
      });
  }

  public addGroup(): void {
    const id: string = this.lprHotlist.id;

    this._dialog
      .open(AddHotlistGroupDialogComponent, {
        data: this.lprHotlist,
      })
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        if (result?.name) {
          this._fetchData(id);
        }
      });
  }

  public onSearchValueChange(value: string): void {
    this._searchQuery = value?.toLowerCase();
    this.reload();
  }

  public addPlates(lprGroup: SnLprHotListGroup): void {
    this._dialog
      .open(ManageLprGroupDialogComponent, {
        data: {
          lprHotlist: this.lprHotlist,
          lprGroup,
        },
        width: '600px',
      })
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.reload();
      });
  }

  public viewPlates(lprGroup: SnLprHotListGroup): void {
    this._dialog
      .open(ManageLprGroupDialogComponent, {
        data: {
          lprObjects: this.lprObjects,
          lprHotlist: this.lprHotlist,
          lprGroup,
        },
        width: '600px',
      })
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._fetchData(this.lprHotlist.id);
      });
  }

  public reload(): void {
    this._fetchData(this.lprHotlist.id);
  }

  public loadPlates(): void {
    this._fetchPlates();
  }

  private _fetchData(id: string): void {
    this.isLoading = true;
    merge(this.matSort.sortChange, this.paginator.page)
      .pipe(
        startWith({}),
        switchMap(() => {
          return this._lprHotlistGroupsAPI
            .getGroups(id, {
              ordering: new SortParameters({
                active: this.sort,
                direction: this.sortDirection,
              }).convertedActive,
              page: this.paginator.pageIndex + 1,
              pageSize: this.pageSize,
              search: this._searchQuery,
            })
            .pipe(catchError(() => of(null)));
        }),
        map((data) => {
          if (data === null) {
            return [];
          }
          this.resultsLength = data.count;
          return data;
        }),
        takeUntil(this._destroy$),
      )
      .subscribe((res) => {
        this.dataSource = new MatTableDataSource(res.results);
        this.dataSource.paginator = this.paginator;
        this.isLoading = false;
      });
  }

  private _fetchPlates(): void {
    this.isLoading = true;
    merge(this.matSort.sortChange, this.paginator.page)
      .pipe(
        startWith({}),
        switchMap(() => {
          return this._lprPlatesAPI.getGroupsLprPlates({}).pipe(catchError(() => of(null)));
        }),
        map((data) => {
          if (data === null) {
            return [];
          }
          this.resultsLength = data.count;
          return data;
        }),
        takeUntil(this._destroy$),
      )
      .subscribe((res) => {
        this.dataSource = new MatTableDataSource(res.results);
        this.dataSource.paginator = this.paginator;
        this.isLoading = false;
      });
  }

  private _getColumns(): string[] {
    return ['name', 'num_plates', 'actions'];
  }
}
